import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器 - 添加token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || ''
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response) => {
    // 检查是否是业务错误（允许200和201都算成功）
    if (response.data.code !== 200 && response.data.code !== 201) {
      // 创建错误对象，包含完整的响应数据
      const error = new Error(response.data.message || '请求失败')
      error.response = {
        status: response.status,
        data: response.data // 包含 code, message, data 等完整信息
      }
      throw error
    }
    // 返回完整响应对象，包括code和message
    return response.data
  },
  (error) => {
    // 处理 401 未授权 - 登录状态失效
    if (error.response?.status === 401) {
      const authStore = useAuthStore()
      authStore.logout()

      // 使用更友好的提示
      const message = error.response?.data?.message || '登录状态已失效，请重新登录'

      // 尝试使用Vant Notify，如果失败则静默处理
      try {
        if (typeof window !== 'undefined' && window.VantNotify) {
          window.VantNotify({
            type: 'warning',
            message,
            duration: 3000
          })
        }
      } catch (notifyError) {
        // 静默处理
      }

      // 延迟跳转，让用户看到提示
      setTimeout(() => {
        window.location.href = '/login'
      }, 1500)

      return Promise.reject(error)
    }

    // 对于其他错误（如400, 404, 500等），优先使用后端返回的message
    // 创建新的错误对象，确保message字段包含后端返回的具体错误信息
    if (error.response?.data?.message) {
      const customError = new Error(error.response.data.message)
      customError.response = error.response
      customError.code = error.response.data.code
      return Promise.reject(customError)
    }

    return Promise.reject(error)
  }
)

// 认证服务
export const authService = {
  login(username, password) {
    return api.post('/auth/login', { username, password })
  },
  logout() {
    return api.post('/auth/logout')
  },
  getProfile() {
    return api.get('/auth/profile')
  },
  refreshToken(token) {
    return api.post('/auth/refresh', { token })
  }
}

// 配置服务 - 使用 /receipts/configs 获取包含班级类型和定价的完整配置
export const configService = {
  getAll() {
    return api.get('/receipts/configs')
  }
}

// 收据服务
export const receiptService = {
  create(data) {
    return api.post('/receipts', data)
  },
  query(params) {
    return api.post('/receipts/query', params)
  },
  getReceipt(id) {
    return api.get(`/receipts/${id}`)
  },
  getDetail(id) {
    return api.get(`/receipts/${id}/detail`)
  },
  getImage(id) {
    return api.get(`/receipts/${id}/image`)
  },
  share(id, method) {
    return api.post(`/receipts/${id}/share`, { method })
  },
  // 查询订金收据信息（用于关联）
  queryDepositReceipt(receiptId) {
    return api.get(`/receipts/deposit/${receiptId}`)
  },
  // 更新订金收据状态（关联正式收据后）
  updateDepositReceiptAfterUse(depositReceiptId, data) {
    return api.put(`/receipts/deposit/${depositReceiptId}/use`, data)
  },
  // 获取订金收据修复信息
  getDepositReceiptFixInfo(depositReceiptId) {
    return api.get(`/receipts/deposit/${depositReceiptId}/fix-info`)
  },
  // 检查订金收据备份文件状态
  checkDepositReceiptBackup(depositReceiptId) {
    return api.get(`/receipts/deposit/${depositReceiptId}/backup-status`)
  }
}

// 学员服务
export const studentService = {
  getList() {
    return api.get('/students')
  },
  getDetail(id) {
    return api.get(`/students/${id}`)
  },
  create(data) {
    return api.post('/students', data)
  }
}

// 业绩服务
export const performanceService = {
  getOverview(period = 'month') {
    return api.get('/performance/overview', { params: { period } })
  },
  getList(params) {
    return api.get('/performance', { params })
  }
}

// 公开收据查询服务（无需认证）
export const receiptPublicService = {
  query(params) {
    // 创建临时axios实例，跳过认证拦截器
    const publicApi = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    // 响应拦截器 - 处理错误
    publicApi.interceptors.response.use(
      (response) => {
        // 如果响应状态码是 200，但业务代码不是 200 或 201，抛出错误
        if (response.data.code !== 200 && response.data.code !== 201) {
          throw new Error(response.data.message || '请求失败')
        }
        return response.data
      },
      (error) => {
        // 处理 HTTP 错误（如 400, 404, 500 等）
        // 优先使用后端返回的 message
        if (error.response?.data?.message) {
          return Promise.reject(new Error(error.response.data.message))
        }
        // 如果没有 message，使用默认错误信息
        return Promise.reject(new Error(error.message || '请求失败'))
      }
    )
    
    return publicApi.post('/receipts/public/query', params)
  },
  verify(params) {
    // 创建临时axios实例，跳过认证拦截器
    const publicApi = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    // 响应拦截器 - 处理错误
    publicApi.interceptors.response.use(
      (response) => {
        // 如果响应状态码是 200，但业务代码不是 200 或 201，抛出错误
        if (response.data.code !== 200 && response.data.code !== 201) {
          throw new Error(response.data.message || '请求失败')
        }
        return response.data
      },
      (error) => {
        // 处理 HTTP 错误（如 400, 404, 500 等）
        // 优先使用后端返回的 message
        if (error.response?.data?.message) {
          return Promise.reject(new Error(error.response.data.message))
        }
        // 如果没有 message，使用默认错误信息
        return Promise.reject(new Error(error.message || '请求失败'))
      }
    )
    
    return publicApi.post('/receipts/public/verify', params)
  }
}

export default api
